import React, { useContext, useState, useEffect } from 'react';
import { Card } from 'src/components';
import { MerchantProfileContext } from 'src/context/merchant-profile-context';
import { getDocumentDetails, getDocument } from 'src/services/documents.service';
import TealiumEvents from 'src/helpers/tealium-events';
import MiniAppEvents from 'src/helpers/miniapp-events';
import PDFViewer from 'src/components/pdf-viewer/pdf-viewer';
import HeaderNav from '../header-nav/header-nav';

import { CactusImage } from 'src/assets/images';
import useTechnicalErrorSelfServiceHandler from 'src/hooks/useTechnicalErrorSelfService';
import './my-documents.scss';

const inMiniProgram = MiniAppEvents.isInMiniApp();

const edgeOpenOrSave = (data, filename, saveOnly = false) => {
  // Required Microsoft Edge <= 44.18362.449.0
  const dataBase64 = data.replace(`${filename}|data:application/pdf;base64,`, '');
  const byteCharacters = atob(dataBase64);
  let byteNumbers = new Array(byteCharacters.length);

  for (var i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'application/pdf' });

  if (saveOnly) {
    window.navigator.msSaveBlob(blob, filename);
  } else {
    window.navigator.msSaveOrOpenBlob(blob, filename);
  }
};

const isEdgeBrowser = () => {
  return window.navigator && window.navigator.msSaveOrOpenBlob && window.navigator.msSaveBlob;
};

const downloadFile = async (documentDetails, handleTechnicalError) => {
  try {
    const data = await getDocument(documentDetails.documentId, handleTechnicalError, false, true);

    if (inMiniProgram) {
      //MINI: Implement mini-app specific functionality
      MiniAppEvents.sendMessage({
        messageType: 'download_from_base64',
        data: data.dataCopy.replace(`${documentDetails.filename}|`, ''),
      });
    } else if (isEdgeBrowser()) {
      edgeOpenOrSave(data.dataCopy, documentDetails.filename, true);
    } else {
      const linkSource = data.dataCopy.replace(`${documentDetails.filename}|`, '');
      const downloadLink = document.createElement('a');
      const fileName = `${documentDetails.filename}`;
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    }
  } catch (e) {
    console.error(e);
  }
};

const viewFile = async (documentDetails, setPdfFile, setShowPDF, handleTechnicalError) => {
  try {
    const data = await getDocument(documentDetails.documentId, handleTechnicalError, false, true);

    if (data.dataCopy) {
      if (inMiniProgram) {
        setPdfFile(data.dataCopy.replace(`${documentDetails.filename}|`, ''));
        setShowPDF(true);
      } else if (isEdgeBrowser()) {
        edgeOpenOrSave(data.dataCopy, documentDetails.filename);
      } else {
        let pdfWindow = window.open('');
        pdfWindow.document.write(
          "<iframe width='100%' height='100%' src='" +
            data.dataCopy.replace(`${documentDetails.filename}|`, '') +
            "'></iframe>",
        );
        pdfWindow.document.title = documentDetails.filename;
      }
    }
  } catch (e) {
    console.error(e);
  }
};

export default function MyDocuments() {
  const handleTechnicalError = useTechnicalErrorSelfServiceHandler();
  const { merchant } = useContext(MerchantProfileContext);
  const [documents, setDocuments] = useState([]);
  const [pdfFile, setPdfFile] = useState('');
  const [showPDF, setShowPDF] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const displayIncrements = 6;
  const [displayedAmount, setDisplayedAmount] = useState(displayIncrements);

  useEffect(() => {
    TealiumEvents.myDocumentsPageLoadNewStyle();

    const abortController = new AbortController();
    const retrieveDocuments = async (handleTechnicalError) => {
      setIsLoading(true);
      try {
        const _documents = await getDocumentDetails(
          abortController,
          handleTechnicalError,
          false,
          true,
        );
        if (!abortController.signal.aborted) {
          setDocuments(_documents || []);
        }
      } catch (e) {
        console.error(e);
      }
      setIsLoading(false);
    };
    retrieveDocuments(handleTechnicalError);
  }, []);

  return (
    <div className="MyDocuments" data-testid="MyDocuments">
      <HeaderNav
        title={merchant?.companyName ? `Hi, welcome back ${merchant?.companyName}` : ''}
        screenName="My documents"
      >
        {inMiniProgram && (
          <PDFViewer show={showPDF} file={pdfFile} onHide={() => setShowPDF(false)}></PDFViewer>
        )}
        <h1 className="page-title has-text-left is-hidden-mobile has-margin-bottom-4">
          My documents
        </h1>
        <Card
          className="card-table has-mobile-margin-top-7 is-hidden-mobile"
          stretchVertical={true}
        >
          <table className="table documents-table is-fullwidth">
            <thead>
              <tr>
                <th style={{ paddingLeft: '31px' }}>Type</th>
                <th>Category</th>
                <th>File name</th>
                <th style={{ textAlign: 'center' }}>
                  {!isLoading && (!documents || documents.length === 0) ? 'Download' : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                documents &&
                documents.length > 0 &&
                documents.slice(0, displayedAmount).map((document, index) => (
                  <tr key={index} className="is-hidden-mobile">
                    <td automationid={`MyDocuments_TD_DocumentType_${index}`}>
                      <div className="columns is-vcentered is-gapless">
                        <div className="column table-data-icon is-narrow">
                          <div className="icon-circle-container">
                            <span className="item-icon-red is-icon-4 icon-document"></span>
                          </div>
                        </div>
                        <u className="column" style={{ textAlign: 'left', marginLeft: '20px' }}>
                          {document.typeName}
                        </u>
                      </div>
                    </td>
                    <td automationid={`MyDocuments_TD_DocumentCategory_${index}`}>
                      {document.categoryName}
                    </td>
                    <td
                      automationid={`MyDocuments_Link_DocumentFileName_${index}`}
                      onClick={() => {
                        viewFile(document, setPdfFile, setShowPDF, handleTechnicalError);
                      }}
                      className="add-cursor-pointer"
                    >
                      {document.filename}
                    </td>
                    <td className="table-data-icon is-narrow">
                      <div
                        className="icon-download-container is-clickable"
                        automationid={`MyDocuments_Link_DocumentDownload_${index}`}
                        onClick={() => {
                          TealiumEvents.myDocumentsPageClickDownloadButton();
                          downloadFile(document, handleTechnicalError);
                        }}
                      >
                        <span className="item-icon-white is-icon-7 icon-download"></span>
                      </div>
                    </td>
                  </tr>
                ))}
              {!isLoading && (!documents || documents.length === 0) && (
                <>
                  <tr>
                    <td colSpan="7" className="has-text-centered no-data-image-container">
                      <img src={CactusImage} className="no-data-image" />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="7" className="has-text-centered no-data-text-container">
                      <span className="title">No documents to display yet</span>
                    </td>
                  </tr>
                </>
              )}
              {isLoading && (
                <tr>
                  <td colSpan="7" className="has-text-centered">
                    Loading...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>

        <div className="is-hidden-tablet">
          {!isLoading && documents && documents.length > 0 && (
            <Card className="card-table has-mobile-margin-top-7" stretchVertical={true}>
              <table className="table is-hidden-tablet documents-mobile-table">
                <tbody>
                  {documents.slice(0, displayedAmount).map((document, index) => (
                    <tr key={index}>
                      <td className="table-data-icon is-narrow">
                        <div className="icon-circle-container">
                          <span className="item-icon-red is-icon-4 icon-bill"></span>
                        </div>
                      </td>
                      <td>
                        <span
                          className="table-data-sub-text"
                          automationid={`MyDocuments_TD_DocumentCategory_${index}`}
                        >
                          {document.categoryName}
                        </span>
                        <br />
                        <span
                          onClick={() => {
                            viewFile(document, setPdfFile, setShowPDF, handleTechnicalError);
                          }}
                          className="table-data-description add-cursor-pointer"
                        >
                          {document.filename}
                        </span>
                        <br />
                        <span className="table-data-sub-text">{document.typeName}</span>
                        <br />
                      </td>
                      {!inMiniProgram && (
                        <td className="table-data-button">
                          <button
                            className="is-pulled-right"
                            onClick={() => {
                              TealiumEvents.myDocumentsPageClickDownloadButton();
                              downloadFile(document, handleTechnicalError);
                            }}
                          >
                            Download
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {isLoading && (
            <Card className="card-table has-mobile-margin-top-7" stretchVertical={true}>
              <table className="table is-fullwidth is-hidden-tablet has-background-transparent">
                <tbody>
                  <tr>
                    <td className="has-text-centered table-data-description">Loading...</td>
                  </tr>
                </tbody>
              </table>
            </Card>
          )}
          {!isLoading && (!documents || documents.length === 0) && (
            <table className="table is-fullwidth is-hidden-tablet">
              <tbody>
                <tr>
                  <td className="has-text-centered no-data-image-container">
                    <img src={CactusImage} className="no-data-image" />
                  </td>
                </tr>
                <tr>
                  <td className="has-text-centered no-data-text-container">
                    <span className="title">No documents to display yet</span>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        {displayedAmount < documents.length && (
          <div
            className="load-more-container"
            onClick={() => {
              TealiumEvents.myDocumentsPageClickLoadMore();

              const newDisplayedAmount = Math.min(
                displayedAmount + displayIncrements,
                documents.length,
              );
              setDisplayedAmount(newDisplayedAmount);
            }}
          >
            Load more&nbsp;
            <span className="icon icon-menu-chevron-down is-icon-7"></span>
          </div>
        )}
      </HeaderNav>
    </div>
  );
}
